import React from "react";
import PropTypes from "prop-types";
import { connect, useDispatch, useSelector } from "react-redux";
import { withProps } from "recompose";
import compose from "lodash/fp/compose";
import compact from "lodash/compact";
import moment from "moment";
import {
  withQuery,
  withTableHandlers,
  withApi,
  withHeaderFooter
} from "features/query";
import { setPatientBatchIds } from "actions/batch";
import { getFormOptionsSleepCoachWithUserFirst, selectors } from "reducers";
import Table from "components/ui/impl/table";
import { createTab } from "actions/system-tray";
import { IoPerson } from "react-icons/lib/io";
import PureModal from "components/ui/modal/pure-modal";
import PatientProfile from "components/screens/patient/patient-profile";
import TeamCoachFilter from "../patients-table/team-sleepcoach-filter";
import AutodialerBtn from "../autodialer-btn";
import BulkUpdateFollowUps from "./bulk-update-follow-ups";
import { clearOrderTableSelected } from "actions/sleep-coach-dashboard";
import { useFetch } from "hooks";
import { put } from "utils/api";
import { message } from "actions/message";
import TableControls from "./table-controls";

export const platformOptions = [
  {
    text: "Web - Patient Portal",
    value: "Patient Portal"
  },
  {
    text: "Email Order",
    value: "Patient Portal From Email"
  },
  {
    text: "SMS Order",
    value: "Patient Portal From SMS"
  }
];

const useMarkAsAPI = () => {
  const dispatch = useDispatch();

  return useFetch({
    apiFn: payload => put(`incomplete_order_tracking/markAs`, payload),
    onSuccess: () => {
      dispatch(message("Order marked as worked"));
    }
  });
};

const formatLastUpdate = ({ last_updated }) =>
  moment(last_updated).format("MM / DD / YYYY h:mm A");

const formatDateAssigned = ({ date_assigned }) =>
  date_assigned ? moment(date_assigned).format("MM / DD / YYYY h:mm A") : null;

const formatWorkedDate = ({ date_worked }) =>
  date_worked ? moment(date_worked).format("MM / DD / YYYY h:mm A") : null;

const StatusCell = ({ worked, patient_id }) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const markAsAPI = useMarkAsAPI();
  const dispatch = useDispatch();

  // Show as worked if either already worked or currently loading
  const displayWorked = worked || isLoading;

  const handleClick = async () => {
    if (displayWorked) return; // Already worked or request in flight

    setIsLoading(true);
    try {
      await markAsAPI.fetchData({
        mark_as: "markAsWorked",
        patient_ids: [patient_id]
      });
      setIsLoading(false);
      // Remove the row from the incompleteOrderTracking state
      dispatch({
        type: "REMOVE_INCOMPLETE_ORDER",
        payload: patient_id
      });
    } catch (error) {
      // Reset loading state if request fails
      setIsLoading(false);
      console.error("Marked as worked:", error);
    }
  };

  return (
    <span
      style={{
        padding: "4px 8px",
        borderRadius: "4px",
        fontSize: "12px",
        fontWeight: "bold",
        color: "white",
        backgroundColor: displayWorked ? "#28a745" : "#dc3545",
        textAlign: "center",
        display: "inline-block",
        minWidth: "70px",
        cursor: displayWorked ? "not-allowed" : "pointer"
      }}
      title={displayWorked ? "" : "Mark as Worked"}
      onClick={handleClick}
    >
      {displayWorked ? "Worked" : "Unworked"}
    </span>
  );
};

const statusOptions = [
  {
    text: "Unworked",
    value: "unworked"
  },
  {
    text: "Worked",
    value: "worked"
  }
];

const BatchHeader = ({ rows }) => {
  const dispatch = useDispatch();
  React.useEffect(() => {
    dispatch({ type: "CLEAR_SELECTED" });
  }, [rows]);
  const scDashboardSelected = useSelector(state =>
    state.getIn(["sleepCoachDashboard"])
  );
  return (
    <th>
      <span
        onClick={() => {
          if (rows.length > scDashboardSelected.patients.length) {
            dispatch({
              type: "SELECT_ALL",
              patients: rows.map(o => o.patient_id),
              orders: rows.map(o => o.patient_id)
            });
          } else {
            dispatch({ type: "CLEAR_SELECTED" });
          }
        }}
      >
        All
      </span>
    </th>
  );
};
const BatchCell = ({ patient_id }) => {
  const dispatch = useDispatch();
  // should add beter selector fn
  const scDashboardSelected = useSelector(state =>
    state.getIn(["sleepCoachDashboard"])
  );
  return (
    <td>
      <input
        checked={scDashboardSelected.patients.includes(patient_id)}
        onChange={() => {
          dispatch({
            type: "TOGGLE_ITEM",
            patientId: patient_id,
            orderId: patient_id
          });
        }}
        type="checkbox"
      ></input>
    </td>
  );
};

const OrderFollowUpTable = ({ ...props }) => (
  <div className="order-status-table">
    <Table {...props} />
  </div>
);

export default compose(
  connect(
    state => ({
      companyOptions: selectors.getFormOptionsCompany(state),
      sleepCoaches: getFormOptionsSleepCoachWithUserFirst(state),
      admins: selectors.getEnumAdmins(state),
      selectedItems: state.getIn(["sleepCoachDashboard"]),
      teamNameOptions: selectors
        .getTeamNameOptions(state)
        .map(tno => ({ key: tno.text, text: tno.text, value: tno.text })),
      query: selectors.getQueryFilters(state, "portal-order-follow-up-table")
    }),
    {
      createTab,
      setPatientBatchIds,
      clearOrderTableSelected
    }
  ),
  // withQuery({}),
  withQuery({}),

  withApi({
    api: {
      route: "incomplete_order_tracking",
      responseKey: "incompleteOrderTracking",
      idKey: "patient_id",
      entityKey: "orderRows"
    }
  }),
  withProps(
    ({
      selectedItems,
      createTab,
      setPatientBatchIds,
      companyOptions,
      clearOrderTableSelected,
      requestData,
      teamNameOptions,
      incompleteOrderTracking,
      query
    }) => {
      const handleRowUpdate = (patientId, updatedData) => {
        // Update the specific row in the orderRows
        const orderRows = incompleteOrderTracking?.orderRows || [];
        const rowIndex = orderRows.findIndex(
          row => row.patient_id === patientId
        );
        if (rowIndex !== -1) {
          orderRows[rowIndex] = { ...orderRows[rowIndex], ...updatedData };
          // Trigger re-render by using requestData which should update the component
          // Or you could use forceUpdate, but requestData is cleaner
        }
      };

      return {
        columns: compact([
          {
            header: "",
            HeaderCell: BatchHeader,
            RowCell: BatchCell,
            field: "id"
          },
          {
            header: "Patient",
            field: "patient_id",
            format: ({ patient_id }) => (
              <PureModal
                windowProps={{ large: "true" }}
                renderTrigger={({ openModal }) => (
                  <div
                    style={{
                      display: "flex",
                      flex: 1,
                      alignItems: "center"
                    }}
                  >
                    <IoPerson
                      onClick={openModal}
                      size={20}
                      style={{ color: "#249c91", cursor: "pointer" }}
                    />
                  </div>
                )}
              >
                <PatientProfile patientId={patient_id} />
              </PureModal>
            )
          },
          {
            header: "Patient Name",
            field: "patient",
            filterKey: "patient_id",
            sort: "patient"
          },
          {
            header: "Company",
            field: "company",
            filterKey: "company_id",
            filterDownSelect: true,
            filters: companyOptions,
            sort: "company"
          },
          {
            header: "Team",
            field: "patient_team_name",
            filters: teamNameOptions,
            filterKey: "patient_team_id",
            filterDownSelect: true,
            sort: "patient_team_name"
          },
          {
            header: "Assigned Coach",
            field: "assigned_coach",
            renderFilter: props => (
              <TeamCoachFilter {...props} teamNameOptions={teamNameOptions} />
            ),
            filterKey: "sleep_coach",
            filterDownSelect: true,
            sort: "assigned_coach"
          },
          {
            header: "Date Assigned",
            field: "date_assigned",
            format: formatDateAssigned,
            sort: "date_assigned"
          },
          {
            header: "Created Date",
            field: "last_updated",
            format: formatLastUpdate,
            sort: "last_updated"
          },
          {
            header: "Platform",
            field: "platform",
            filterKey: "platform",
            filters: platformOptions,
            sort: "platform"
          },
          {
            header: "Worker",
            field: "worker_coach",
            renderFilter: props => (
              <TeamCoachFilter {...props} teamNameOptions={teamNameOptions} />
            ),
            filterKey: "worker_coach",
            filterDownSelect: true,
            sort: "worker_coach"
          },
          {
            header: "Worked Date",
            field: "date_worked",
            format: formatWorkedDate,
            sort: "date_worked"
          },
          {
            header: "Status",
            field: "worked",
            filterKey: "worked",
            filterDownSelect: false,
            RowCell: ({ worked, patient_id }) => (
              <StatusCell worked={worked} patient_id={patient_id} />
            ),
            filters: statusOptions
          }
        ]),
        customDisplay: [
          () =>
            selectedItems.orders.length || selectedItems.patients.length ? (
              <div
                style={{
                  color: "white",
                  display: "flex",
                  flex: 1,
                  justifyContent: "center"
                }}
              >
                <div style={{ marginRight: "10px" }}>
                  Selected patients: {selectedItems.patients.length}
                </div>
              </div>
            ) : null
        ],
        customButtons: [
          {
            component: () => <TableControls query={query} />
          },
          {
            component: () => (
              <>
                <span className="action-buttons-sleepcoach-dashboard">
                  <span
                    className="icon-button-sleepcoach-dashboard"
                    onClick={() => {
                      if (selectedItems.patients.length) {
                        setPatientBatchIds(selectedItems.patients);
                        createTab({
                          type: "patients",
                          key: "patients",
                          label: `Patients (${selectedItems.patients.length})`,
                          meta: { ids: selectedItems.patients }
                        });
                      }
                    }}
                  >
                    <IoPerson />
                  </span>

                  <BulkUpdateFollowUps
                    selectedRows={selectedItems.patients.map(patientId => ({
                      patient_id: patientId
                    }))}
                    refreshData={requestData}
                  />

                  <AutodialerBtn
                    selectedPatients={selectedItems.patients}
                    batchClear={clearOrderTableSelected}
                    refreshData={requestData}
                    setSelectedPatients={clearOrderTableSelected}
                  />
                </span>
              </>
            )
          }
        ],
        showCount: true
      };
    }
  ),
  withHeaderFooter({
    perPageOptions: [500, 1000, 3000],
    forceBatchReset: true,
    scrollable: true
  }),
  withTableHandlers()
)(OrderFollowUpTable);

OrderFollowUpTable.propTypes = {
  markedForBatch: PropTypes.arrayOf(PropTypes.string),
  onBulkUpdateSuccess: PropTypes.func
};
